import { Box, Button, CardActions, Chip, Container, Divider, Grid, Typography } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import PaperBox from "components/PaperBox"
import { SkeletonCard } from "components/skeleton/SkeletonCard"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import "moment/locale/sv"
import "moment/locale/da"
import {
  StyledCard,
  StyledCardContent,
  StyledDiv,
  StyledGreyGrid,
  StyledGrid,
  StyledLink
} from "pages/news/components/NewsMain.styled"
import React, { useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import PaginationForPages from "components/paginationForPages/PaginationForPages"
import moment from "moment"
import SortForm from "pages/news/components/SortForm/SortForm"
import NoNewsCard from "pages/news/components/NoNewsCard/NoNewsCard"
import FilterForm from "pages/news/components/FilterForm"
import { useTranslation } from "react-i18next"

const truncate = (str) => {
  return str.length > 249 ? str.substring(0, 250) + "..." : str
}

// initial page states:
const initialState = {
  sortby: 0,
  direction: 0,
  showAll: true,
  companyIds: [],
  departmentIds: [],
  pageSize: 10,
  page: 1
}

const NewsMain = () => {
  const loadingContext = useContext(GlobalLoaderContext)
  const appContext = useContext(AppContext)
  const isNewsAdmin = hasRole(appContext.appUser, allRoles.NewsAdmin)
  const rows = [1, 2, 3, 4, 5, 6]

  const { selectedLanguage } = useContext(AppContext)
  const locale = selectedLanguage === "en" ? "en-gb" : selectedLanguage
  const { t } = useTranslation("translation")

  // if we're newsadmin show the option to "show all"
  // which means if you _don't_ select any companies you can see the whole customers news
  // and you also get a new list of companies to select from
  // otherwise you can select from your own companies

  const [state, setState] = useState(initialState)

  const [showAllCompanies, setShowAllCompanies] = useState(false)

  // the list of available companies to select from
  const [companies, setCompanies] = useState(appContext.appUser.customer.companies)

  const [adminCompanies, setAdminCompanies] = useState([])

  // if we're newsadmin and select "show all = true", then we fetch the customer to get "all companies"
  const [newsAdminCustomerResult, newsAdminCustomerFuncs] = useApi()

  const [listResult, getListFuncs] = useApi()
  const [page, setPage] = useState(1)
  const pageSize = 10

  // first load, show all articles
  // and load the admin companies if we can
  useEffect(() => {
    getListFuncs.post(apiEndpoints.news.listarticles, { pageSize: pageSize })

    if (isNewsAdmin) newsAdminCustomerFuncs.get(apiEndpoints.newsadmin.getcustomer)
  }, [getListFuncs, newsAdminCustomerFuncs, isNewsAdmin])

  useEffect(() => {
    getListFuncs.post(apiEndpoints.news.listarticles, { ...state, pageSize, page })
  }, [page])
  // when we get data, set the admincompanies
  // it will stay empty if we're not admín
  useEffect(() => {
    if (newsAdminCustomerResult.status === 2) setAdminCompanies(newsAdminCustomerResult.data.companies)
  }, [newsAdminCustomerResult])

  useEffect(() => {
    loadingContext.setLoading(() => listResult.status === 1)
  })

  const count = Math.ceil(listResult.status === 2 && listResult.data.pageCount)
  const setPageNum = (e, page) => {
    setPage(page)
  }

  // when filter change, reload list of articles
  const onChange = React.useCallback(
    (newState) => {
      setPage(1)
      getListFuncs.post(apiEndpoints.news.listarticles, {
        direction: newState.sortby,
        showAll: showAllCompanies,
        companyIds: newState.companyIds,
        departmentIds: newState.departmentIds
      })
      setState((prevState) => {
        return {
          ...prevState,
          direction: newState.sortby,
          showAll: showAllCompanies,
          companyIds: newState.companyIds,
          departmentIds: newState.departmentIds
        }
      })
    },
    [getListFuncs, showAllCompanies]
  )

  useEffect(() => {
    if (showAllCompanies) {
      setCompanies(adminCompanies)
    } else {
      setCompanies(appContext.appUser.customer.companies)
    }
  }, [showAllCompanies, adminCompanies, appContext.appUser.customer.companies])

  return (
    <>
      <Container>
        <Box mb={5}>
          <Grid item container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2">{t("news.latest_news", "Latest news")}</Typography>
          </Grid>
        </Box>
        <Grid container spacing={4} direction="row" justifyContent="space-between">
          <Grid item xs={12} sm={12} md={8}>
            <Box mb={2}>
              <PaginationForPages count={count} page={page} onChange={setPageNum} size="medium" />
            </Box>
            {listResult.status === 2 ? (
              <>
                {listResult.data.results.length < 1 && <NoNewsCard />}
                <Grid container spacing={2} alignItems="flex-end">
                  {listResult.data.results.map((item, key) => {
                    const formattedDate = moment(item.publishedDate).locale(locale).format("DD MMMM YYYY")

                    return (
                      <Grid key={key} item xs={12}>
                        <StyledLink to={allRoutes.NewsListRead(item.id)}>
                          <StyledCard>
                            <StyledDiv>
                              <StyledCardContent>
                                {item.priority && (
                                  <Box mb={2}>
                                    <Chip label={t("global.important", "Important")} color="primary" />
                                  </Box>
                                )}
                                <Typography variant="h4" gutterBottom>
                                  {item.subject}
                                </Typography>{" "}
                                <Typography variant="bodySmall" color="textSecondary" component="p">
                                  {truncate(item.subTitle)}
                                </Typography>
                                <Box mt={2}>
                                  <Divider light mb={2} />
                                </Box>
                                <CardActions>
                                  <StyledGrid container direction="row">
                                    <StyledGreyGrid item>
                                      {t("global.by", "By")} {item.publishedByUserFullName}
                                      {" • "}
                                      {formattedDate}
                                    </StyledGreyGrid>
                                    <Button
                                      component={Link}
                                      size="small"
                                      color="primary"
                                      to={allRoutes.NewsListRead(item.id)}>
                                      {t("global.buttons.readmore", "Read more")}
                                    </Button>
                                  </StyledGrid>
                                </CardActions>
                              </StyledCardContent>
                            </StyledDiv>
                          </StyledCard>
                        </StyledLink>
                      </Grid>
                    )
                  })}
                </Grid>
              </>
            ) : (
              rows.map(() => (
                <Grid item pb={4}>
                  <SkeletonCard padding="20px 30px" />
                </Grid>
              ))
            )}
            <Box mt={2}>
              <PaginationForPages count={count} page={page} onChange={setPageNum} size="medium" />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <PaperBox marginY={0}>
              <SortForm onChange={onChange} setState={setState} state={state} />
              <Divider light />
              <FilterForm
                onChange={onChange}
                showShowAll={isNewsAdmin}
                setState={setState}
                state={state}
                setShowAllCompanies={setShowAllCompanies}
                companies={companies}
                showAllCompanies={showAllCompanies}
              />
            </PaperBox>
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default NewsMain
